import { Field, FormGrid, Input } from './ui/form'

/**
 * The postal address block, shared by Customer and Reseller.
 *
 * Both carry the same five fields, so this is written once - the API says the same thing
 * with IPostalAddress.
 *
 * The tax number lives on the entity beside them but is not part of the address: it
 * identifies the company, not where its post goes, so it is a separate export that each
 * party renders in its own identity panel.
 */

export interface PostalAddress {
  addressLine1: string | null
  addressLine2: string | null
  addressLine3: string | null
  postcode: string | null
  addressCountry: string | null
  taxIdentificationNo: string | null
}

/** Where the tax number is asked for. Malaysia issues an LHDN TIN; the others do not yet. */
const TAX_ID_COUNTRIES: Record<string, string> = {
  MY: 'TIN number',
}

export function AddressFields({
  value,
  onChange,
  disabled,
  changed,
}: {
  value: Partial<PostalAddress>
  onChange: <K extends keyof PostalAddress>(key: K, next: PostalAddress[K]) => void
  disabled?: boolean
  /** Which of these have been edited and not saved. Supplied by the page's dirty state. */
  changed?: (key: keyof PostalAddress) => boolean
}) {
  return (
    <FormGrid>
      <Field label="Address line 1" changed={changed?.('addressLine1')}>
        {(id) => (
          <Input
            id={id}
            disabled={disabled}
            value={value.addressLine1 ?? ''}
            onChange={(event) => onChange('addressLine1', event.target.value)}
          />
        )}
      </Field>
      <Field label="Address line 2" changed={changed?.('addressLine2')}>
        {(id) => (
          <Input
            id={id}
            disabled={disabled}
            value={value.addressLine2 ?? ''}
            onChange={(event) => onChange('addressLine2', event.target.value)}
          />
        )}
      </Field>
      <Field label="Address line 3" changed={changed?.('addressLine3')}>
        {(id) => (
          <Input
            id={id}
            disabled={disabled}
            value={value.addressLine3 ?? ''}
            onChange={(event) => onChange('addressLine3', event.target.value)}
          />
        )}
      </Field>
      <Field label="Postcode" changed={changed?.('postcode')}>
        {(id) => (
          <Input
            id={id}
            disabled={disabled}
            value={value.postcode ?? ''}
            onChange={(event) => onChange('postcode', event.target.value)}
          />
        )}
      </Field>
      <Field
        label="Country"
        changed={changed?.('addressCountry')}
        hint="Where the post goes. Separate from the country this record belongs to, which sets its pricing and approvals."
      >
        {(id) => (
          <Input
            id={id}
            disabled={disabled}
            value={value.addressCountry ?? ''}
            onChange={(event) => onChange('addressCountry', event.target.value)}
          />
        )}
      </Field>
    </FormGrid>
  )
}

/**
 * The tax number, where one is issued.
 *
 * Renders nothing at all outside those countries: an empty "Tax ID" on a Singapore record
 * is a field nobody can fill and a question nobody can answer.
 */
export function TaxIdField({
  value,
  onChange,
  disabled,
  changed,
  countryIsoCode,
}: {
  value: string | null | undefined
  onChange: (next: string) => void
  disabled?: boolean
  changed?: boolean
  countryIsoCode?: string
}) {
  const label = countryIsoCode ? TAX_ID_COUNTRIES[countryIsoCode] : undefined

  if (!label) return null

  return (
    <Field label={label} changed={changed} hint="LHDN tax identification number.">
      {(id) => (
        <Input
          id={id}
          disabled={disabled}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  )
}
